// Smart typing in the note's textarea: what each shortcut would do to the text,
// worked out as a value rather than done to a field.
//
// PURE. Every function here takes the text and the selection and returns an
// `Edit` - a range to replace, what to put there, and where the selection lands
// after. Nothing here touches the DOM, so every rule is testable without a
// browser, and `NoteEditor.tsx` is left with one job: applying an `Edit` in the
// one way that does not break undo (see `applyEdit` there, and the paragraph
// below, which is the reason this split exists at all).
//
//   THE UNDO TRAP, MEASURED. Mutating a textarea by assigning `.value` - which
//   is what an ordinary React state update does - DESTROYS the browser's native
//   undo stack. Measured in Chrome: after a value-assignment edit `Cmd+Z` did
//   nothing; after `document.execCommand('insertText', …)` it restored the
//   original text, kept React's state in sync, and fired `onChange` again so the
//   existing autosave saved the undo too. Breaking `Cmd+Z` in a notes app is
//   worse than shipping none of this, and "undo on every destructive action" is
//   a correctness requirement in this project rather than a feature (AGENTS.md).
//   `docs/research/notes-formatting.md` §7 carries the measurement.
//
// SO EVERY EDIT BELOW IS SHAPED TO BE INSERTABLE: where there is a choice, it
// prefers replacing a range with non-empty text over deleting one, because an
// insert of the empty string is the one shape `insertText` is not reliable for.
// `continueList` ending a list is the one place a deletion is unavoidable, and
// it swallows the preceding newline and puts it back so that even that is an
// insert everywhere except on the very first line of a note.

// THE TASK BOX IS READ FROM `note-markdown.ts`, not spelled again here. A
// shortcut that thought `- [x]buy milk` was a ticked task while the Read view
// drew it as a bullet would be one note behaving as two, so the vocabulary keeps
// the single home it claims.
import {
  inlineIsFullyFormatted,
  inlineOwnsPartialRange,
  inlineText,
  lineIsProse,
  linkLabelIsSupported,
  linkOwnsRange,
  scanAddress,
  splitLine,
  TASK_BOX,
  type InlineMarker,
} from './note-markdown.ts';

/** A replacement, and where the selection goes afterwards. */
export interface Edit {
  /** The range in the CURRENT value that this replaces. */
  start: number;
  end: number;
  /** What goes there. Empty only where a deletion is genuinely what is meant. */
  text: string;
  /** The selection once the edit has landed, as offsets into the NEW value. */
  select: [number, number];
}

/** Bold and italic, and nothing else wraps a selection. */
export type Wrap = InlineMarker;

/** A list marker with whatever indents it, and whatever follows it. */
const LIST_LINE = /^([ \t]*)(?:([-*+])|(\d{1,9})([.)]))([ \t]+)(.*)$/;

/** One level of indent, and what Tab inserts. */
const STEP = '  ';

function lineStartAt(value: string, at: number): number {
  return value.lastIndexOf('\n', at - 1) + 1;
}

function lineEndAt(value: string, at: number): number {
  const next = value.indexOf('\n', at);
  return next === -1 ? value.length : next;
}

function textPreservingEdit(value: string, edit: Edit): Edit | null {
  const from = lineStartAt(value, edit.start);
  const to = lineEndAt(value, edit.end);
  const before = value.slice(from, to);
  const after = value.slice(from, edit.start) + edit.text + value.slice(edit.end, to);
  const read = (text: string) => text.split('\n').map(inlineText).join('\n');
  return read(before) === read(after) ? edit : null;
}

/**
 * Enter, on a line that is a list item.
 *
 * Null means "this is not a list line" and the browser's own Enter runs, which
 * is the important half: a shortcut that fires everywhere is a textarea that
 * behaves oddly in prose.
 *
 * ONLY WITH A COLLAPSED CARET. Enter over a selection is a delete and an insert,
 * and continuing a list across it needs a second answer for which line's marker
 * to copy; the browser's own Enter is right there and is not wrong.
 */
export function continueList(value: string, at: number): Edit | null {
  const from = lineStartAt(value, at);
  const to = lineEndAt(value, at);
  const line = value.slice(from, to);
  const listed = LIST_LINE.exec(line);
  if (listed === null) return null;

  const lead = listed[1] ?? '';
  const bullet = listed[2];
  const number = listed[3];
  const delimiter = listed[4] ?? '.';
  const gap = listed[5] ?? ' ';
  const rest = listed[6] ?? '';
  const box = bullet === undefined ? null : TASK_BOX.exec(rest);
  const content = box === null ? rest : (box[2] ?? '');
  const markerEnd = from + line.length - rest.length;
  const contentStart = markerEnd + (box === null ? 0 : rest.length - content.length);
  if (at < contentStart) return null;

  // AN EMPTY MARKER ENDS THE LIST, which is the universal behaviour and the only
  // way out of one without reaching for the mouse. The marker goes and the caret
  // stays on the line it was on - Enter does not also add a line here.
  if (content.trim() === '') {
    if (from === 0) return { start: 0, end: to, text: '', select: [0, 0] };
    // The preceding newline is swallowed and put straight back, so this is an
    // INSERT rather than a delete. See the header.
    return { start: from - 1, end: to, text: '\n', select: [from, from] };
  }

  const marker =
    bullet !== undefined
      ? // A new task starts unticked. Copying `[x]` down would tick something
        // nobody has done. The space after the box is part of the box - without
        // it the line the next keystroke lands on is not a task at all.
        `${bullet}${gap}${box === null ? '' : '[ ] '}`
      : `${Number(number) + 1}${delimiter}${gap}`;

  return {
    start: at,
    end: at,
    text: `\n${lead}${marker}`,
    select: [at + 1 + lead.length + marker.length, at + 1 + lead.length + marker.length],
  };
}

/**
 * Tab and Shift-Tab, on the list line the caret is in.
 *
 * NULL IS THE POINT OF THIS FUNCTION. Tab is only taken when the caret is on a
 * list line, so a Tab in prose still moves focus and the field is never a
 * keyboard trap; and Shift-Tab on a line with nothing left to outdent is null
 * too, which is what gives a keyboard user the way BACK out of a list they are
 * inside. Both directions are asserted in `web/test/note-typing.test.ts`.
 */
export function indentLines(
  value: string,
  start: number,
  end: number,
  outdent: boolean,
): Edit | null {
  const from = lineStartAt(value, start);
  const rangeEnd = end > start ? end - 1 : end;
  const to = lineEndAt(value, rangeEnd);
  // The line the caret is ON decides whether Tab belongs to this field at all.
  // A selection that happens to reach into a list from a paragraph does not
  // make Tab a list key.
  if (!LIST_LINE.test(value.slice(from, lineEndAt(value, start)))) return null;

  const lines = value.slice(from, to).split('\n');
  const deltas: number[] = [];
  const next = lines.map((line) => {
    if (!LIST_LINE.test(line)) {
      deltas.push(0);
      return line;
    }
    if (!outdent) {
      deltas.push(STEP.length);
      return STEP + line;
    }
    const taken = /^(\t| {1,2})/.exec(line);
    deltas.push(taken === null ? 0 : -taken[0].length);
    return taken === null ? line : line.slice(taken[0].length);
  });

  const total = deltas.reduce((sum, d) => sum + d, 0);
  if (total === 0) return null;

  // Where the selection lands: each end moves by everything that changed above
  // it, plus its own line's change - and never past the start of its own line,
  // which is where an outdent would otherwise drag a caret sitting in the
  // whitespace it just removed.
  const move = (at: number): number => {
    let offset = from;
    let shift = 0;
    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i] ?? '';
      const lineEnd = offset + line.length;
      if (at <= lineEnd) return Math.max(at + shift + (deltas[i] ?? 0), offset + shift);
      shift += deltas[i] ?? 0;
      offset = lineEnd + 1;
    }
    return at + total;
  };

  return { start: from, end: to, text: next.join('\n'), select: [move(start), move(end)] };
}

/**
 * A line's own words, with the whitespace either side of them held aside.
 *
 * WHITESPACE MAY NOT END UP INSIDE A MARKER: `**  hello**` is not bold, because
 * a marker with a space against its inside is not a marker - that is
 * `parseInline`'s flanking rule, and writing syntax the renderer then ignores is
 * the whole defect this file's shortcuts keep being asked not to cause.
 */
function bodyOf(content: string): { lead: string; body: string; tail: string } {
  const lead = /^\s*/.exec(content)?.[0] ?? '';
  const bare = content.slice(lead.length);
  const tail = /\s*$/.exec(bare)?.[0] ?? '';
  return { lead, body: bare.slice(0, bare.length - tail.length), tail };
}

/**
 * One line, split into what may be written round and what may not.
 *
 * `before` is everything a shortcut has to leave alone and write AFTER - the
 * indent, the bullet, the box, the hashes, the `>` - taken from `splitLine` in
 * `note-markdown.ts` so the renderer and the shortcut cannot hold two opinions
 * about where a marker ends.
 */
function partsOf(
  line: string,
  start: number,
  end: number,
): { line: string; before: string; body: string; tail: string } {
  const { marker } = splitLine(line);
  const beforeEnd = Math.min(end, marker.length);
  const before = start < beforeEnd ? line.slice(start, beforeEnd) : '';
  const contentStart = Math.max(start, marker.length);
  const content = contentStart < end ? line.slice(contentStart, end) : '';
  const { lead, body, tail } = bodyOf(content);
  return { line: line.slice(start, end), before: before + lead, body, tail };
}

function selectedParts(value: string, start: number, end: number) {
  let at = start;
  return value
    .slice(start, end)
    .split('\n')
    .map((selected) => {
      const from = lineStartAt(value, at);
      const line = value.slice(from, lineEndAt(value, at));
      const localStart = at - from;
      at += selected.length + 1;
      return partsOf(line, localStart, localStart + selected.length);
    });
}

/**
 * The selection, narrowed to the words it is allowed to put markers round.
 *
 * A selection that reaches back over its line's marker - Cmd+A on a one-bullet
 * note is the everyday way to get one - would otherwise swallow it, and
 * `**- milk**` is a bold paragraph with a dash in it rather than a bold list
 * item. The whitespace at either end goes the same way and for the reason in
 * `bodyOf` above.
 */
function contentRange(value: string, start: number, end: number): [number, number, '' | ' '] {
  const from = lineStartAt(value, start);
  const lineEnd = lineEndAt(value, start);
  const { marker, emptyContentPrefix } = splitLine(value.slice(from, lineEnd));
  let a = Math.max(start, from + marker.length);
  let b = Math.max(a, end);
  while (a < b && /\s/.test(value[a] ?? '')) a += 1;
  while (b > a && /\s/.test(value[b - 1] ?? '')) b -= 1;
  const prefix = start === end && a === lineEnd ? emptyContentPrefix : '';
  return [a, b, prefix];
}

/**
 * Whether a line already carries this marker on both ends of its own words.
 *
 * Asked of the line's CONTENT, because that is what gets wrapped - so
 * `- **alpha**` is a wrapped bullet and pressing bold on it takes the emphasis
 * off rather than the list.
 */
type WrapState = 'plain' | 'wrapped' | 'ambiguous';

function wrapState(body: string, marker: Wrap): WrapState {
  const character = marker[0] ?? '';
  let opening = 0;
  let closing = 0;
  while (body[opening] === character) opening += 1;
  while (body[body.length - closing - 1] === character) closing += 1;

  const carriesMarker =
    marker === '*'
      ? opening % 2 === 1 && closing % 2 === 1
      : opening >= marker.length && closing >= marker.length;
  if (!carriesMarker) return 'plain';
  if (opening + closing >= body.length) return 'ambiguous';

  const middle = body.slice(opening, body.length - closing);
  if (marker !== '`' && (/^\s/.test(middle) || /\s$/.test(middle))) return 'ambiguous';
  return middle.includes(marker) ? 'ambiguous' : 'wrapped';
}

function isWrapped(body: string, marker: Wrap): boolean {
  return wrapState(body, marker) === 'wrapped';
}

function wouldMergeMarker(body: string, marker: Wrap): boolean {
  const character = marker[0] ?? '';
  return body.startsWith(character) || body.endsWith(character);
}

/**
 * A selection that spans more than one line, wrapped LINE BY LINE.
 *
 * WHY IT CANNOT BE ONE PAIR OF MARKERS ROUND THE LOT. `parseInline` runs per
 * line - `parseNote` groups prose lines into one block and parses each of them
 * independently - so an inline run may not cross a newline. Wrapping the whole
 * selection wrote `**alpha\nbeta**`, where NEITHER `**` ever closes: the note
 * drew literal asterisks and no bold anywhere, so the shortcut damaged the text
 * and appeared to do nothing at the same time.
 *
 * ONE EDIT, NOT ONE PER LINE, and that is a hard requirement rather than a
 * tidiness. `applyEdit` turns an `Edit` into a single `execCommand('insertText')`
 * and the browser's undo stack takes one step per call - so four lines wrapped by
 * four edits would cost four presses of Cmd+Z, which trades a small annoyance for
 * a much worse one in the app that leans on undo hardest.
 *
 * A BLANK LINE IS SKIPPED rather than wrapped: `** **` means nothing and renders
 * as the asterisks somebody can see, which is the defect this exists to fix. A
 * line with a marker and no words - an empty bullet - is skipped for the same
 * reason.
 *
 * THE MARKER IS WRITTEN ROUND THE LINE'S CONTENT AND NOT ROUND THE LINE. `-
 * alpha` becomes `- **alpha**`, and a task keeps its box, a heading its hashes,
 * a quote its `>`. Wrapping the whole line instead left `**- alpha**`, where the
 * bullet is no longer followed by a space - so the list stopped being a list and
 * the note drew bold paragraphs with dashes in them.
 *
 * UNWRAPPING IS SYMMETRIC. If every line with anything on it is already wrapped,
 * the markers come off each - so the key pressed twice gives back exactly the
 * text that was there rather than doubling it.
 */
function wrapEachLine(value: string, start: number, end: number, marker: Wrap): Edit | null {
  const width = marker.length;
  const parts = selectedParts(value, start, end);
  const filled = parts.filter((part) => part.body !== '');
  // No words anywhere in it, so there is nothing to emphasise. Null hands the
  // key back rather than writing markers round nothing.
  if (filled.length === 0) return null;
  const states = filled.map((part) => wrapState(part.body, marker));
  if (
    states.some((state) => state === 'ambiguous') ||
    filled.some(
      (part, index) =>
        states[index] === 'plain' &&
        (wouldMergeMarker(part.body, marker) ||
          !inlineIsFullyFormatted(marker + part.body + marker, marker)),
    )
  ) {
    return null;
  }
  const off = states.every((state) => state === 'wrapped');

  const next = parts
    .map(({ line, before, body, tail }) => {
      if (body === '') return line;
      const wrapped = isWrapped(body, marker);
      const content = off ? body.slice(width, -width) : wrapped ? body : marker + body + marker;
      return `${before}${content}${tail}`;
    })
    .join('\n');

  // The same lines stay selected: a formatting key that collapsed the selection
  // to the top of the note is its own small annoyance.
  return textPreservingEdit(value, {
    start,
    end,
    text: next,
    select: [start, start + next.length],
  });
}

/**
 * Cmd+B, Cmd+I - wrap the selection, or take the wrapping off again.
 *
 * The markers are looked for on BOTH sides of the selection boundary, because
 * double-clicking a word inside `**bold**` selects the word and not its
 * markers, and pressing bold again there has to mean "stop being bold".
 *
 * NULL WHERE THERE ARE NO WORDS IN THE SELECTION. Dragging across a run of
 * spaces, or across a bullet and nothing else, is not a request to emphasise
 * anything - and `** **` is not emphasis, it is two pairs of asterisks somebody
 * can see. A collapsed caret is the one exception, and it is a different ask.
 */
export function wrapSelection(
  value: string,
  start: number,
  end: number,
  marker: Wrap,
): Edit | null {
  const width = marker.length;

  if (inlineOwnsPartialRange(value, start, end)) return null;

  // More than one line is a different edit, for a reason the renderer decides.
  if (value.slice(start, end).includes('\n')) return wrapEachLine(value, start, end, marker);

  // A collapsed caret stays where it is in content and moves to the content
  // boundary if it is inside a block marker, so the marker remains intact.
  const [from, to, prefix] = contentRange(value, start, end);
  if (start !== end && from === to) return null;
  const selected = value.slice(from, to);

  // Markers OUTSIDE the selection - the double-clicked-word case.
  const openingStars = /\*+$/.exec(value.slice(0, from))?.[0].length ?? 0;
  const closingStars = /^\*+/.exec(value.slice(to))?.[0].length ?? 0;
  if (
    value.slice(from - width, from) === marker &&
    value.slice(to, to + width) === marker &&
    (marker !== '*' || (openingStars % 2 === 1 && closingStars % 2 === 1))
  ) {
    const state = wrapState(value.slice(from - width, to + width), marker);
    if (state === 'ambiguous') return null;
    if (state === 'wrapped') {
      return textPreservingEdit(value, {
        start: from - width,
        end: to + width,
        text: selected,
        select: [from - width, from - width + selected.length],
      });
    }
  }

  // Markers INSIDE it - the case where the whole `**bold**` was selected.
  const selectedState = wrapState(selected, marker);
  if (selectedState === 'ambiguous') return null;
  if (selectedState === 'wrapped') {
    const bare = selected.slice(width, -width);
    return textPreservingEdit(value, {
      start: from,
      end: to,
      text: bare,
      select: [from, from + bare.length],
    });
  }
  if (
    selected !== '' &&
    (wouldMergeMarker(selected, marker) ||
      !inlineIsFullyFormatted(marker + selected + marker, marker))
  ) {
    return null;
  }

  // Nothing selected: the markers go in and the caret goes between them, which
  // is what makes Cmd+B before typing mean what it means everywhere else.
  if (selected === '') {
    const character = marker[0] ?? '';
    if (value[from - 1] === character || value[from] === character) return null;
    return {
      start: from,
      end: to,
      text: prefix + marker + marker,
      select: [from + prefix.length + width, from + prefix.length + width],
    };
  }

  return textPreservingEdit(value, {
    start: from,
    end: to,
    text: marker + selected + marker,
    select: [from + width, from + width + selected.length],
  });
}

/**
 * A selection that spans more than one line, LINKED LINE BY LINE.
 *
 * A LINK LIVES ON ONE LINE: `parseInline` wants the `]` and the `(…)` on the
 * line the `[` opened on, so `[alpha\nbeta]()` is not one link across two lines,
 * it is no link at all and two lines of literal brackets. The answer is the
 * wrap's answer, for the wrap's reason - one link per line, the blank ones
 * skipped, the block markers left where they are, and ONE `Edit` so the whole
 * thing is one press of Cmd+Z.
 */
function linkEachLine(value: string, start: number, end: number, url?: string): Edit | null {
  const parts = selectedParts(value, start, end);
  const filled = parts.filter((part) => part.body !== '');
  if (filled.length === 0 || filled.some((part) => !linkLabelIsSupported(part.body))) return null;

  // NEVER LEAVE THE PERSON'S NEXT ACTION DESTRUCTIVE, and that one principle is
  // why this lands the caret somewhere the wrap does not. After Cmd+B the
  // selection IS what was formatted and nothing is typed next, so keeping it is
  // right. After Cmd+K the next thing anybody does is type the address - and a
  // region left selected turns that first keystroke into the deletion of every
  // line in it. So the caret collapses into the FIRST line's parentheses, which
  // is where the typing was going anyway.
  let caret = -1;
  let at = start;
  const next = parts
    .map(({ line, before, body, tail }) => {
      if (body === '') {
        at += line.length + 1;
        return line;
      }
      const made = `${before}[${body}](${url ?? ''})${tail}`;
      if (caret === -1 && url === undefined) caret = at + before.length + body.length + 3;
      at += made.length + 1;
      return made;
    })
    .join('\n');

  // With the address already known there is nothing left to type, so the caret
  // goes after the work, exactly as the single-line paste does.
  const lands = caret === -1 ? start + next.length : caret;
  return { start, end, text: next, select: [lands, lands] };
}

/**
 * Cmd+K, and the same edit a pasted URL over a selection makes.
 *
 * With a `url`, the link is finished and the caret goes after it - that is the
 * paste, and it is the single most-loved small thing in this category. Without
 * one the caret lands where the address goes, or between the brackets when
 * there was nothing selected to name it.
 *
 * IT NAMES THE LINE'S WORDS AND NOT ITS MARKER, the same rule `wrapSelection`
 * keeps and through the same `splitLine`: `- alpha` becomes `- [alpha]()`,
 * because `[- alpha]()` is a link whose text begins with a dash on a line that
 * has stopped being a list.
 */
export function linkSelection(
  value: string,
  start: number,
  end: number,
  url?: string,
): Edit | null {
  if (linkOwnsRange(value, start, end) || inlineOwnsPartialRange(value, start, end)) return null;
  if (value.slice(start, end).includes('\n')) return linkEachLine(value, start, end, url);

  const [from, to, prefix] = contentRange(value, start, end);
  // `[ ](url)` names nothing, the same reason the wrap gives back a selection
  // with no words in it.
  if (start !== end && from === to) return null;
  const selected = value.slice(from, to);
  if (!linkLabelIsSupported(selected)) return null;
  const text = `${prefix}[${selected}](${url ?? ''})`;
  if (url !== undefined) {
    const after = from + text.length;
    return { start: from, end: to, text, select: [after, after] };
  }
  const caret =
    selected === '' ? from + prefix.length + 1 : from + prefix.length + selected.length + 3;
  return { start: from, end: to, text, select: [caret, caret] };
}

/**
 * Cmd+Enter - tick the line the caret is on, and make it a task first if it is
 * not one yet.
 *
 * THIS WRITES `- [x]` INTO THE NOTE AND IS NOT A COMPLETION. A note is text; the
 * tick that counts toward a day is the day screen's, against a routine. Two
 * things that look alike and are not, so it is said here rather than left to be
 * assumed.
 */
export function toggleTask(value: string, at: number): Edit | null {
  const from = lineStartAt(value, at);
  const to = lineEndAt(value, at);
  const line = value.slice(from, to);
  const listed = LIST_LINE.exec(line);

  // A NUMBERED list is left alone: `1. [ ]` is not in this vocabulary, and
  // quietly turning somebody's numbered steps into bullets is not a tick.
  if (listed !== null && listed[2] === undefined) return null;

  if (listed !== null) {
    const rest = listed[6] ?? '';
    const box = TASK_BOX.exec(rest);
    const markerEnd = from + (listed[1] ?? '').length + 1 + (listed[5] ?? '').length;
    if (box !== null) {
      // One character moves, and the caret does not: somebody ticking a line
      // they are still typing on should not lose their place.
      const boxAt = markerEnd + 1;
      return { start: boxAt, end: boxAt + 1, text: box[1] === ' ' ? 'x' : ' ', select: [at, at] };
    }
    // The caret never lands INSIDE the box that was just written: a caret
    // parked before the marker has not moved, so it goes to where the words
    // start rather than being carried four characters into `[ ] `. Same clamp
    // as the plain-line branch below.
    const insert = '[ ] ';
    const caret = Math.max(at, markerEnd) + insert.length;
    return { start: markerEnd, end: markerEnd, text: insert, select: [caret, caret] };
  }

  if (!lineIsProse(line)) return null;

  // Not a list line at all: it becomes a task, keeping whatever it is indented
  // by.
  const lead = /^[ \t]*/.exec(line)?.[0] ?? '';
  const insert = '- [ ] ';
  const put = from + lead.length;
  const caret = Math.max(at, put) + insert.length;
  return { start: put, end: put, text: insert, select: [caret, caret] };
}

/**
 * Whether what was pasted is an address rather than words.
 *
 * Deliberately narrow: one token, one of the schemes this note's links may
 * carry. A paste that is not certainly a URL is an ordinary paste, because
 * turning somebody's pasted paragraph into `[selection](paragraph)` is a
 * surprise with no undo they would think to reach for.
 *
 * AND THE WHOLE TOKEN HAS TO BE AN ADDRESS BY `scanAddress`'S RECKONING,
 * which is what stops this and the renderer holding two opinions about one
 * rule. An address the parser would cut short must not be written as a link
 * here: `…/Mercury_(planet)` is one because parentheses balance, and something
 * ending in a stray `)` is not - and either way both sides now say the same.
 */
export function isUrl(text: string): boolean {
  const one = text.trim();
  const address = scanAddress(one);
  return (
    one !== '' &&
    !/\s/.test(one) &&
    address.length === one.length &&
    address.openParentheses === 0 &&
    /^(https?:\/\/|mailto:)\S+$/i.test(one)
  );
}
